import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import { saveCustomer, saveVisit } from '../services';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { useEstablishmentStore } from '../store/establishment';
import { parseDateString } from '../utils/date';

export default function CustomerRegistration() {
  const { phone } = useParams();
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { establishment } = useEstablishmentStore();

  const formatBirthDate = (input: string) => {
    const numbers = input.replace(/\D/g, '');
    
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  };

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBirthDate(e.target.value);
    if (formatted.length <= 10) {
      setBirthDate(formatted);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!establishment || !phone || !name.trim() || !birthDate) return;

    const formattedDate = parseDateString(birthDate);
    if (!formattedDate) {
      setError('Data de nascimento inválida');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const now = new Date().toISOString();

      await saveCustomer(establishment.id, {
        name: name.trim(),
        phone,
        email: '',
        document: '',
        birthDate: formattedDate,
        points: 0,
        totalVisits: 0,
        visits: [],
        createdAt: now
      });

      await saveVisit(establishment.id, phone, {
        date: now,
        points: establishment.settings.pointsPerVisit,
        visitNumber: 1,
        timestamp: now,
        pointsEarned: establishment.settings.pointsPerVisit,
        totalPointsAfter: establishment.settings.pointsPerVisit
      });

      navigate(`/points-summary/${phone}`);
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Ocorreu um erro ao registrar. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!establishment) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-500 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl w-full max-w-md p-6">
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="w-5 h-5 mr-1" />
          Voltar
        </button>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Novo Cadastro</h1>
          <p className="text-gray-600">Preencha seus dados para continuar</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Nome completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Digite seu nome completo"
            required
            autoFocus
            disabled={loading}
          />

          <div className="relative">
            <Input
              label="Data de nascimento"
              value={birthDate}
              onChange={handleBirthDateChange}
              placeholder="DD/MM/AAAA"
              required
              disabled={loading}
              maxLength={10}
              icon={<Calendar className="w-5 h-5 text-gray-400" />}
            />
          </div>

          <Button
            type="submit"
            loading={loading}
            disabled={loading || !name.trim() || !birthDate}
            className="w-full"
          >
            Cadastrar e Continuar
          </Button>
        </form>
      </div>
    </div>
  );
}